import React from "react";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import { globalStyle } from "../../global/Utils";

// Green for Active, red for Inactive; anything else the server answers (Not
// Registered, Not Found) is neutral gray.
const TONES = {
  Active: "border-green-500 bg-green-50 text-green-700",
  Inactive: "border-red-500 bg-red-50 text-red-700",
};
const NEUTRAL = "border-gray-400 bg-gray-50 text-gray-600";
const EMPTY = "border-gray-200 bg-white text-gray-600";

/**
 * A saved status as a small pill, coloured like the boxes - for the buyer
 * list. "-" for a buyer that was never checked.
 */
export const TaxStatusChip = ({ status }) =>
  status ? (
    <span
      className={`inline-block px-2 py-0.5 rounded-full border text-xs font-semibold ${
        TONES[status] || NEUTRAL
      }`}
    >
      {status}
    </span>
  ) : (
    "-"
  );

/**
 * One live status on the Add Buyer form. `check` is its slice of
 * BuyerReducer.buyerTaxStatus: { isChecking, status, error }.
 * `onRefresh` (optional): asks again; shown as an icon by the title, except
 * while an error offers Retry instead.
 */
export const TaxStatusBox = ({ title, check, idleText, onRetry, onRefresh }) => {
  const { isChecking, status, error } = check;
  const tone = status ? TONES[status] || NEUTRAL : EMPTY;
  return (
    <section
      aria-label={title}
      aria-live="polite"
      aria-busy={isChecking}
      className={`rounded-lg border-2 p-4 shadow-sm ${tone}`}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-600">{title}</h3>
        {onRefresh && !error && (
          <Tooltip title={isChecking ? "Checking..." : "Check again"}>
            <span>
              <IconButton
                size="small"
                aria-label={`Refresh ${title}`}
                disabled={isChecking}
                onClick={onRefresh}
                sx={{ color: "black", margin: "-6px -6px -6px 0" }}
              >
                <SyncIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>
      {isChecking ? (
        <div className="mt-2 flex items-center gap-2 text-gray-600">
          <CircularProgress size={18} />
          <span>Checking...</span>
        </div>
      ) : error ? (
        <div className="mt-2">
          <p className="text-sm text-red-600">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 px-3 py-1 rounded text-white text-sm"
            style={{
              background: globalStyle.buttonColor,
              fontFamily: globalStyle.fontFamilyButton,
            }}
          >
            Retry
          </button>
        </div>
      ) : status ? (
        <p className="mt-1 text-xl font-bold">{status}</p>
      ) : (
        <p className="mt-2 text-sm text-gray-500">{idleText}</p>
      )}
    </section>
  );
};
